import { types, getEnv, flow, SnapshotOut, Instance } from 'mobx-state-tree';
import { ProgressStateModel, IGroupedApiCtx, CancelError, errorIsCancel, errorIsInApiRequest, ProgressStateModelForStore, protoUtils, ICalendarEvents, ICalendarEventsWithTs } from 'eTip--common';
import { ISportsBettingLiveApi } from '../../api/live/api';
import { observable } from 'mobx';
import { CalendarEventStore } from './../calendarEvent';
import { translation } from 'easit--localization';

export interface ISportFilterItem {
    categoryId: number;
    graphicsPresentationId: number;
    label: string;
}

enum CalendarEventFlags {
    None = 0,
    HasActivePrematch = 1
}

const ShowAllFilterId = -1;

export const CalendarStore = types
    .model({
        state: types.optional(ProgressStateModelForStore, ProgressStateModel.NotInitialized),
        date: types.maybeNull(types.Date),
        events: types.optional(types.array(CalendarEventStore), []),
        lastReloadTime: types.optional(types.number, -1),
        datesToFilter: types.optional(types.array(types.Date), []),
        currentDate: types.maybeNull(types.Date),
        selectedSportIds: types.optional(types.array(types.number), [])
    }).views(self => ({
        get filteredEvents(): Instance<typeof CalendarEventStore>[] {
            if (self.selectedSportIds && (self.selectedSportIds.length > 0 && self.selectedSportIds[0] !== ShowAllFilterId)) {
                return self.events.filter(event => { return self.selectedSportIds.indexOf(Number(event.sportCategoryId)) > -1 });
            }
            else {
                return self.events
            }
        },
        get availableSports(): ISportFilterItem[] {
            let auxSports = new Map();
            auxSports.set(ShowAllFilterId, <ISportFilterItem>{
                categoryId: ShowAllFilterId,
                graphicsPresentationId: -1,
                label: translation().all
            })
            self.events.forEach(event => {
                auxSports.set(event.sportCategoryId, <ISportFilterItem>{
                    categoryId: Number(event.sportCategoryId),
                    graphicsPresentationId: event.graphicsPresentationId,
                    label: event.sportName
                })
            });
            return Array.from(auxSports.values());
        },
    }))
    .actions(self => {

        let env = getEnv(self);
        let synchroCtx: IGroupedApiCtx<ISportsBettingLiveApi> | null = null;

        const loadAsync: (asFullLoad: boolean, date: Date) => Promise<void> = flow(function* (asFullLoad: boolean, date: Date) {
            let ctx: IGroupedApiCtx<ISportsBettingLiveApi> | null = null;
            try {
                if (synchroCtx && synchroCtx.isOpen()) {
                    if (!asFullLoad) {
                        throw new CancelError(); //does nothing, already waiting for response
                    }
                    synchroCtx.cancel('Load has been canceled.');
                    synchroCtx = null;
                }
                if (asFullLoad) {
                    self.state = ProgressStateModel.Loading;
                }

                ctx = env.api.open();
                synchroCtx = ctx;
                if (asFullLoad) {
                    self.lastReloadTime = 0;
                }
                const response: ICalendarEventsWithTs = yield ctx.api.getCalendarAsync(
                    date,
                    self.lastReloadTime
                );

                if (response) { // response==null means nochange
                    self.lastReloadTime = response.Timestamp;
                    setData(response.Data);
                    self.selectedSportIds = observable([-1]);
                }

                self.state = ProgressStateModel.Success;
            } catch (error) {
                if (errorIsCancel(error)) {
                    throw error;
                }
                env.loggingService.error(error);
                if (asFullLoad || !errorIsInApiRequest(error)) {
                    self.state = ProgressStateModel.Failed;
                }
                throw error;
            } finally {
                if (ctx === synchroCtx) {
                    synchroCtx = null;
                }
            }
        });

        /**
         @returns object of touched ids
         */
        const setData = (apiData: ICalendarEvents): void => {
            let result = observable([]);

            if (apiData.Events) {
                apiData.Events.forEach(item => {
                    let store = CalendarEventStore.create(<SnapshotOut<typeof CalendarEventStore>>{
                        id: item.Id,
                        name: item.Name,
                        graphicsPresentationId: item.GraphicsPresentationId,
                        categoryPathName: item.Path,
                        isLive: item.IsLive,
                        time: protoUtils.protoTimeStamp2Date(item.Date).getTime(),
                        sportName: item.SportName,
                        sportCategoryId: item.SportCategoryId,
                        isVideoStreamAvailable: item.IsVideoStreamAvailable,
                        hasActivePrematch: (item.Flags & CalendarEventFlags.HasActivePrematch) === CalendarEventFlags.HasActivePrematch
                    });
                    result.push(store);
                })
            }

            self.events = result;
        }

        const initializeDates = (): void => {
            let currentDate = new Date();
            let dates = observable([]);
            for (var i = 0; i < 7; i++) {
                dates.push(new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + i));
            }
            self.currentDate = dates[0];
            self.datesToFilter = dates;
        }
        /**
         * Just sets current date, no load.
         */
        const setDateFilter = (value: Date): void => {
            self.currentDate = value;
        }

        const toggleSport = (sportId: number) => {
            if (sportId === ShowAllFilterId) {
                self.selectedSportIds = observable([ShowAllFilterId]);
            }
            else {
                const idx = self.selectedSportIds ? self.selectedSportIds.indexOf(sportId) : -1;
                if (idx === -1) {
                    self.selectedSportIds.push(sportId);
                    const showAllFilterIndex = self.selectedSportIds.indexOf(ShowAllFilterId)
                    if (showAllFilterIndex > -1) {
                        self.selectedSportIds.splice(showAllFilterIndex, 1);
                    }
                }
                else {
                    self.selectedSportIds.splice(idx, 1);
                    if (self.selectedSportIds.length === 0) {
                        self.selectedSportIds.push(ShowAllFilterId);
                    }
                }
            }
        }

        return {
            loadAsync,
            setDateFilter,
            initializeDates,
            toggleSport
        }
    });

export type ICalendarStore = Instance<typeof CalendarStore>;
